import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import { dateTime, humanise, isoDate } from '@/lib/format';
import { Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { StatusChip } from '@/components/ui/StatusChip';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { SelectInput, TextInput } from '@/components/ui/Field';

/**
 * The audit trail.
 *
 * Everything comes from audit_trail() rather than the v_audit_trail view,
 * for two reasons. The function checks the caller's role itself, and it
 * filters and pages in the database — the audit log is the one table here
 * that only grows, and a year of production is not something to send to a
 * tablet so the tablet can filter it.
 *
 * The question this screen answers is "who changed this, and what did it
 * say before", so the row carries the count of changed fields and the
 * drawer shows them one per line, before and after.
 */

interface Change { field: string; old: unknown; new: unknown }

interface AuditRow extends Record<string, unknown> {
  id: number;
  changed_at: string;
  table_name: string;
  record_id: string | null;
  record_label: string;
  action: 'INSERT' | 'UPDATE' | 'DELETE';
  changed_by: string | null;
  changed_by_name: string;
  change_count: number;
  changes: Change[];
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  total_count: number;
}

interface Filters {
  tables?: string[];
  users?: { id: string; name: string }[];
  first_entry?: string | null;
  total?: number;
}

const PAGE = 100;

const ACTION_TONE: Record<string, 'green' | 'blue' | 'red'> = {
  INSERT: 'green', UPDATE: 'blue', DELETE: 'red',
};

const ACTION_WORD: Record<string, string> = {
  INSERT: 'Created', UPDATE: 'Changed', DELETE: 'Deleted',
};

/** Plumbing columns nobody is auditing. */
const NOISE = new Set(['id', 'site_id', 'created_at', 'created_by', 'updated_at',
  'updated_by', 'synced_at', 'deleted_at']);

function today(): string {
  return isoDate();
}

function daysAgo(n: number): string {
  return isoDate(new Date(Date.now() - n * 86_400_000));
}

/** A stored value as a person would read it. */
function show(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (typeof value === 'object') return JSON.stringify(value);
  const s = String(value);
  // A timestamp is far more useful read back as one than as an ISO string.
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) return dateTime(s);
  return s;
}

export function AuditPage() {
  const canRead = useAuthStore((s) => s.hasRole('manager', 'admin'));

  const [rows, setRows] = useState<AuditRow[] | undefined>(undefined);
  const [filters, setFilters] = useState<Filters>({});
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState<AuditRow | null>(null);

  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(today());
  const [table, setTable] = useState('');
  const [user, setUser] = useState('');
  const [action, setAction] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setRows(undefined);
    const { data, error: err } = await supabase.rpc('audit_trail', {
      p_from: from || null,
      p_to: to || null,
      p_table: table || null,
      p_user: user || null,
      p_action: action || null,
      p_search: search || null,
      p_limit: PAGE,
      p_offset: page * PAGE,
    });

    if (err) {
      setError(
        err.code === '42883'
          ? 'The audit trail needs migration 0026 — run "npx supabase db push".'
          : err.message,
      );
      setRows([]);
      return;
    }
    setError(null);
    setRows((data ?? []) as AuditRow[]);
  }, [from, to, table, user, action, search, page]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    void (async () => {
      const { data } = await supabase.rpc('audit_filters');
      setFilters((data ?? {}) as Filters);
    })();
  }, []);

  const total = rows?.[0]?.total_count ?? 0;
  const pages = Math.max(1, Math.ceil(Number(total) / PAGE));

  // Reset to the first page whenever the question changes, or page 4 of the
  // old filter silently becomes an empty page 4 of the new one.
  const resetting = useCallback(<T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setPage(0);
  }, []);

  const columns: Column<AuditRow>[] = useMemo(() => [
    { key: 'changed_at', label: 'When', render: (r) => dateTime(r.changed_at) },
    { key: 'changed_by_name', label: 'Who' },
    {
      key: 'action', label: 'What', align: 'center',
      render: (r) => (
        <StatusChip tone={ACTION_TONE[r.action] ?? 'grey'} dot={false}>
          {ACTION_WORD[r.action] ?? r.action}
        </StatusChip>
      ),
    },
    { key: 'table_name', label: 'Where', render: (r) => humanise(r.table_name) },
    { key: 'record_label', label: 'Record', render: (r) => <strong>{r.record_label}</strong> },
    {
      key: 'change_count', label: 'Fields', align: 'right',
      render: (r) => (r.action === 'UPDATE'
        ? <span className="tabular">{r.change_count}</span>
        : <span className="muted">—</span>),
    },
  ], []);

  if (!canRead) {
    return (
      <div className="placeholder">
        <h2>Not available for your role</h2>
        <p className="muted">The audit trail is limited to managers and administrators.</p>
      </div>
    );
  }

  return (
    <>
      <Panel title="Audit trail">
        <p className="small muted" style={{ marginTop: 0 }}>
          Every change to production, fleet, maintenance, parts, targets, settings and
          users — who made it, when, and what the record said before. Entries are written
          by the database itself and cannot be edited or removed from inside the app.
        </p>

        <div className="grid grid--form">
          <TextInput id="a-from" label="From" type="date"
            value={from} onChange={resetting(setFrom)} />
          <TextInput id="a-to" label="To" type="date"
            value={to} onChange={resetting(setTo)} />
          {/* SelectInput supplies its own empty option, so the "no filter"
              choice is its placeholder rather than an entry in the list —
              two empty options and the component's own wins silently. */}
          <SelectInput id="a-table" label="Section" value={table}
            placeholder="Everything"
            options={(filters.tables ?? []).map((t) => ({ value: t, label: humanise(t) }))}
            onChange={(v) => resetting(setTable)(v ?? '')} />
          <SelectInput id="a-user" label="Who" value={user}
            placeholder="Anyone"
            options={(filters.users ?? []).map((u) => ({ value: u.id, label: u.name }))}
            onChange={(v) => resetting(setUser)(v ?? '')} />
          <SelectInput id="a-action" label="Change" value={action}
            placeholder="Any"
            options={[
              { value: 'INSERT', label: 'Created' },
              { value: 'UPDATE', label: 'Changed' },
              { value: 'DELETE', label: 'Deleted' },
            ]}
            onChange={(v) => resetting(setAction)(v ?? '')} />
          <TextInput id="a-search" label="Record" placeholder="EX-01, BD-0004…"
            value={search} onChange={resetting(setSearch)} />
        </div>

        {error && <div className="login__error">{error}</div>}
      </Panel>

      <Panel
        title={`Changes${total ? ` · ${total}` : ''}`}
        flush
        actions={
          <div className="row" style={{ gap: 'var(--sp-2)', alignItems: 'center' }}>
            <span className="small muted">Page {page + 1} of {pages}</span>
            <Button variant="secondary" disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}>Newer</Button>
            <Button variant="secondary" disabled={page + 1 >= pages}
              onClick={() => setPage((p) => p + 1)}>Older</Button>
          </div>
        }
      >
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(r) => String(r.id)}
          onRowClick={(r) => setOpen(r)}
          defaultSort="changed_at"
          searchPlaceholder="Search this page…"
          empty="No changes recorded for that question."
        />
      </Panel>

      <Drawer
        open={open !== null}
        title={open ? `${ACTION_WORD[open.action] ?? open.action} · ${open.record_label}` : ''}
        subtitle={open ? `${humanise(open.table_name)} · ${dateTime(open.changed_at)} · ${open.changed_by_name}` : ''}
        onClose={() => setOpen(null)}
        width={620}
        footer={<><span className="spacer" /><Button onClick={() => setOpen(null)}>Close</Button></>}
      >
        {open?.action === 'UPDATE' && (
          open.changes.length === 0 ? (
            <p className="small muted">
              Nothing of substance changed — only bookkeeping columns.
            </p>
          ) : (
            <>
              <div className="form-section">What changed</div>
              <div className="col" style={{ gap: 'var(--sp-2)' }}>
                {open.changes.map((c) => (
                  <div key={c.field} className="change">
                    <div className="change__field">{humanise(c.field)}</div>
                    <div className="row" style={{ gap: 'var(--sp-2)', flexWrap: 'wrap', marginTop: 2 }}>
                      <span className="small change__from">{show(c.old)}</span>
                      <span className="small muted">→</span>
                      <span className="small change__to">{show(c.new)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )
        )}

        {open && open.action !== 'UPDATE' && (
          <>
            <div className="form-section">
              {open.action === 'INSERT' ? 'The record as it was created' : 'The record as it was deleted'}
            </div>
            <table className="kv">
              <tbody>
                {Object.entries(
                  (open.action === 'INSERT' ? open.new_values : open.old_values) ?? {},
                )
                  .filter(([k, v]) => !NOISE.has(k) && v !== null && v !== '')
                  .map(([k, v]) => (
                    <tr key={k}>
                      <th>{humanise(k)}</th>
                      <td>{show(v)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </>
        )}

        <div className="form-section">Entry</div>
        <p className="small muted" style={{ marginTop: 0 }}>
          Recorded automatically at {dateTime(open?.changed_at)} against{' '}
          {humanise(open?.table_name)}
          {open?.record_id ? ` (${open.record_id})` : ''}. Audit entries are written by a
          database trigger and there is no route to change one from this application.
        </p>
      </Drawer>
    </>
  );
}
